import { InputTypes, ValueTypes, IFieldDefinition, FieldViews } from 'app/interfaces';
import { Validators } from '@angular/forms';
import { IDataContainerService } from 'app/interfaces/data-container.interface';
import { assistantMetaSnapshot } from 'app/main/components/assistant/assistant-meta.service';

/**
 * modules.ai_assistant: al salvataggio il server crea (o blocca) team e chiave LiteLLM del dominio.
 * Campi piatti: il form di dominio salva le chiavi così come sono.
 */
const fieldsMap: Map<string, IFieldDefinition> = new Map();

fieldsMap.set('enabled', {
	key: 'enabled',
	name: 'Assistente AI',
	inputType: InputTypes.SELECT,
	valueType: ValueTypes.BOOLEAN,
	inputHint: 'Se attivo, gli utenti abilitati trovano l\'assistente nelle sezioni attivate qui sotto (pacchetti, catalogo, risorse, prodotti, partenze).',
	formValidators: [ Validators.required ],
	options: [
		{ label: 'Disattivato', value: false },
		{ label: 'Attivo', value: true }
	]
});

/**
 * Switch per area (registro AssistantAreas lato server, ricevuto da assistant/meta): chiave piatta per area in
 * modules.ai_assistant, assente = attiva. Un solo campo segnaposto: nel form l'input ASSISTANT_AREAS crea un controllo
 * per ogni area (e toglie il segnaposto, che non va salvato); nel dettaglio riassume attive e spente.
 */
const isOff = (value: any): boolean => value === false || value === 'false' || value === 0 || value === '0';

fieldsMap.set('assistant_areas', {
	key: 'assistant_areas',
	name: 'Sezioni attive',
	inputType: InputTypes.ASSISTANT_AREAS,
	valueType: ValueTypes.STRING,
	getDisplayValue(record: any): string {
		const areas = (assistantMetaSnapshot()?.groups || []).flatMap(group => group.areas);
		if(!areas.length) return '';
		const on = areas.filter(area => !isOff(record?.[area.key])).map(area => area.label);
		const off = areas.filter(area => isOff(record?.[area.key])).map(area => area.label);
		return [on.length ? 'Attive: ' + on.join(', ') : '', off.length ? 'Spente: ' + off.join(', ') : ''].filter(Boolean).join('. ');
	}
});

/**
 * Budget in token per periodo (AssistantConfig::TOKEN_BUDGETS lato server: stessi scaglioni, minimo 5 milioni anche se
 * non impostato). Conta domande, risposte e consigli; un turno di chat usa fra 8 e 30 mila token.
 */
const TOKEN_BUDGETS = [5, 10, 20, 50, 100, 200, 500];

fieldsMap.set('max_tokens', {
	key: 'max_tokens',
	name: 'Token per periodo',
	inputType: InputTypes.SELECT,
	valueType: ValueTypes.NUMBER,
	inputHint: '5 milioni di token bastano per circa 200–600 domande. Raggiunto il limite, l\'assistente si ferma fino all\'azzeramento.',
	defaultValue(): number {
		return TOKEN_BUDGETS[0] * 1_000_000;
	},
	options: TOKEN_BUDGETS.map(millions => ({ label: millions + ' milioni di token', value: millions * 1_000_000 }))
});

fieldsMap.set('budget_duration', {
	key: 'budget_duration',
	name: 'Azzeramento del budget',
	inputType: InputTypes.SELECT,
	valueType: ValueTypes.STRING,
	// periodi di calendario (giorno, settimana da lunedì, mese); il vecchio "30d" vale come mese
	defaultValue(): string {
		return '1mo';
	},
	options: [
		{ label: 'Ogni giorno', value: '1d' },
		{ label: 'Ogni settimana', value: '7d' },
		{ label: 'Ogni mese', value: '1mo' }
	],
	printTransform(value: any): string {
		return ({ '1d': 'Ogni giorno', '7d': 'Ogni settimana', '30d': 'Ogni mese', '1mo': 'Ogni mese' } as Record<string, string>)[value] ?? 'Ogni mese';
	}
});

fieldsMap.set('conversation_retention_days', {
	key: 'conversation_retention_days',
	name: 'Conservazione delle conversazioni',
	inputType: InputTypes.SELECT,
	valueType: ValueTypes.NUMBER,
	inputHint: 'Le conversazioni senza nuovi messaggi da più di questo periodo vengono cancellate ogni notte. I consumi restano.',
	defaultValue(): number {
		return 90;
	},
	options: [
		{ label: '30 giorni', value: 30 },
		{ label: '60 giorni', value: 60 },
		{ label: '90 giorni', value: 90 },
		{ label: '6 mesi', value: 180 },
		{ label: '1 anno', value: 365 }
	]
});

fieldsMap.set('scenario', {
	key: 'scenario',
	name: '',
	inputType: InputTypes.HIDDEN,
	valueType: ValueTypes.STRING,
	fieldView: FieldViews.HIDDEN,
	formValidators: [ Validators.required ],
	defaultValue(dataContainerService: IDataContainerService) {
		return 'modules.ai_assistant';
	}
});

export { fieldsMap as aiAssistantFields };
